import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Loader2, RefreshCw, TrendingUp } from 'lucide-react';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import {
  salesDashboardApiService,
  ProductActivity,
  SalesByCategory,
  SalesByCustomer,
  TopProductBySales,
} from '../services/salesDashboardApi';

const PIE_COLORS = ['#3b82f6', '#f97316', '#10b981', '#a855f7', '#ef4444', '#14b8a6', '#f59e0b', '#6366f1'];
const RADIAN = Math.PI / 180;

/** Position value labels outside the slice without clipping at card edges. */
function renderCategoryValueLabel(props: {
  cx?: number;
  cy?: number;
  midAngle?: number;
  outerRadius?: number;
  value?: number;
}) {
  const { cx = 0, cy = 0, midAngle = 0, outerRadius = 0, value } = props;
  if (value == null || Number(value) <= 0) return null;

  const radius = outerRadius + 18;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={500}
    >
      {formatNumberIN(value)}
    </text>
  );
}

function formatCurrency(value: number | null | undefined): string {
  const v = Number(value ?? 0) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(v);
}

function formatNumberIN(value: number | null | undefined): string {
  const v = Number(value ?? 0) || 0;
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getLast30DaysRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  const toISO = (d: Date) => d.toISOString().split('T')[0];
  return { startDate: toISO(start), endDate: toISO(end) };
}

export function SalesDashboard() {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [byCategory, setByCategory] = useState<SalesByCategory[]>([]);
  const [byCustomer, setByCustomer] = useState<SalesByCustomer[]>([]);
  const [topProducts, setTopProducts] = useState<TopProductBySales[]>([]);
  const [productActivity, setProductActivity] = useState<ProductActivity[]>([]);

  const branchId = selectedBranch ?? user?.defaultBranchId;
  const { startDate, endDate } = useMemo(() => getLast30DaysRange(), []);

  const load = async () => {
    try {
      setLoading(true);
      const [c, cust, top, activity] = await Promise.all([
        salesDashboardApiService.getSalesByCategory({ branchId: branchId ?? undefined, startDate, endDate }),
        salesDashboardApiService.getSalesByCustomer({ branchId: branchId ?? undefined, startDate, endDate }),
        salesDashboardApiService.getTopProducts({ branchId: branchId ?? undefined, startDate, endDate, limit: 10 }),
        salesDashboardApiService.getProductActivity({ branchId: branchId ?? undefined, days: 30 }),
      ]);

      setByCategory(Array.isArray(c) ? c : []);
      setByCustomer(Array.isArray(cust) ? cust : []);
      setTopProducts(Array.isArray(top) ? top : []);
      setProductActivity(Array.isArray(activity) ? activity : []);
    } catch (e) {
      toast.error('Failed to load sales dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const pieData = useMemo(() => {
    return (byCategory || [])
      .map((r: any) => ({
        name: String(r.categoryName ?? r.category ?? 'N/A'),
        value:
          Number(
            r.grossSalesAmount ??
              r.netSalesAmount ??
              r.totalSalesValue ??
              r.totalSales ??
              r.totalAmount ??
              0
          ) || 0,
      }))
      .filter((r) => r.value > 0);
  }, [byCategory]);

  const kpis = useMemo(() => {
    const rows = byCategory || [];
    const net = rows.reduce((s: number, r: any) => s + (Number(r.netSalesAmount ?? 0) || 0), 0);
    const tax = rows.reduce((s: number, r: any) => s + (Number(r.taxAmount ?? 0) || 0), 0);
    const gross = rows.reduce(
      (s: number, r: any) =>
        s +
        (Number(
          r.grossSalesAmount ??
            ((Number(r.netSalesAmount ?? 0) || 0) + (Number(r.taxAmount ?? 0) || 0))
        ) || 0),
      0
    );
    return { net, tax, gross };
  }, [byCategory]);

  const customerBarData = useMemo(() => {
    return (byCustomer || [])
      .map((r: any) => ({
        name: String(r.customerName ?? r.customer ?? 'N/A'),
        value:
          Number(
            r.grossSalesAmount ??
              r.netSalesAmount ??
              r.totalSalesValue ??
              r.totalSales ??
              r.totalAmount ??
              0
          ) || 0,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);
  }, [byCustomer]);

  const topProductsBarData = useMemo(() => {
    return (topProducts || [])
      .map((r: any) => ({
        name: String(r.productName ?? r.itemName ?? 'N/A'),
        value:
          Number(
            r.grossSalesAmount ??
              r.netSalesAmount ??
              r.totalSalesValue ??
              r.totalSales ??
              r.totalAmount ??
              0
          ) || 0,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [topProducts]);

  // const inactiveProducts = useMemo(() => {
  //   return (productActivity || []).filter((p: any) => {
  //     const isActive = p.isActive;
  //     if (typeof isActive === 'boolean') return !isActive;
  //     const cnt = Number(p.invoiceCountLastNDays ?? p.invoiceCount ?? 0) || 0;
  //     return cnt <= 0;
  //   });
  // }, [productActivity]);

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl text-gray-900">Sales Dashboard</CardTitle>
              <p className="text-gray-600 text-sm">Last 30 days ({startDate} to {endDate})</p>
            </div>
            <Button
              onClick={load}
              variant="outline"
              size="sm"
              disabled={loading}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="border border-primary-200 bg-white">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Amount</p>
                <p className="text-lg font-semibold text-primary-700">{formatCurrency(kpis.net)}</p>
              </CardContent>
            </Card>
            <Card className="border border-primary-200 bg-white">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Total Tax Amount</p>
                <p className="text-lg font-semibold text-primary-700">{formatCurrency(kpis.tax)}</p>
              </CardContent>
            </Card>
            <Card className="border border-primary-200 bg-white">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Grand Total</p>
                <p className="text-lg font-semibold text-primary-700">{formatCurrency(kpis.gross)}</p>
              </CardContent>
            </Card>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
              <p className="text-gray-600">Loading sales dashboard...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border border-primary-200">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <CardTitle className="text-lg">Sales by Customer</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {customerBarData.length === 0 ? (
                    <div className="text-center text-gray-500 py-10">No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={customerBarData} layout="vertical" margin={{ top: 10, right: 20, left: 80, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tickFormatter={(v) => `${Math.round(Number(v) / 100000)}L`} />
                        <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v: any) => formatCurrency(Number(v) || 0)} />
                        <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
              <Card className="border border-primary-200">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary-600" />
                    Sales by Product Category
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 overflow-visible">
                  {pieData.length === 0 ? (
                    <div className="text-center text-gray-500 py-10">No data</div>
                  ) : (
                    <div className="overflow-visible w-full min-h-[400px]">
                      <ResponsiveContainer width="100%" height={400}>
                        <PieChart margin={{ top: 48, right: 56, bottom: 48, left: 56 }}>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="46%"
                            outerRadius={82}
                            labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                            label={renderCategoryValueLabel}
                          >
                            {pieData.map((_, idx) => (
                              <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v: any) => formatCurrency(Number(v) || 0)} />
                          <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border border-primary-200 lg:col-span-2">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <CardTitle className="text-lg">Top 10 Products by Sales</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {topProductsBarData.length === 0 ? (
                    <div className="text-center text-gray-500 py-10">No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={360}>
                      <BarChart data={topProductsBarData} layout="vertical" margin={{ top: 10, right: 20, left: 100, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tickFormatter={(v) => `${Math.round(Number(v) / 100000)}L`} />
                        <YAxis type="category" dataKey="name" width={220} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v: any) => formatCurrency(Number(v) || 0)} />
                        <Bar dataKey="value" fill="#f97316" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Inactive Products table (disabled for now) */}
              {/* 
              <Card className="border border-primary-200 lg:col-span-2">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <CardTitle className="text-lg">Inactive Products (no invoices in last 30 days)</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {inactiveProducts.length === 0 ? (
                    <div className="p-10 text-center text-gray-500">No inactive products</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead>Product</TableHead>
                            <TableHead className="text-right">Invoices (30d)</TableHead>
                            <TableHead>Last Invoice Date</TableHead>
                            <TableHead className="text-right">Sales (30d)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {inactiveProducts.slice(0, 50).map((p: any, idx: number) => (
                            <TableRow key={p.productId ?? idx} className="hover:bg-primary-50/50">
                              <TableCell className="font-medium">{p.productName ?? p.itemName ?? 'N/A'}</TableCell>
                              <TableCell className="text-right">{Number(p.invoiceCountLastNDays ?? 0) || 0}</TableCell>
                              <TableCell>{formatDate(p.lastInvoiceDate)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(Number(p.totalSalesValueLastNDays ?? 0) || 0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card> 
              */}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

